import dayjs from 'dayjs';

import { DEFAULT_FULL_DATE_FORMAT, DEFAULT_TIME_FORMAT } from 'core/constants';

import { timezoneStore } from 'stores/Timezone';

import { colors } from 'theme/colors';

import { DateTimeValue, formatFloating, formatMoment, isTzFreeDate, readerDayKey } from 'utils/formatters/timezone';

type DueDateDisplay = { name: string; color: string };

const TODAY_COLOR = 'rgb(76, 175, 80)';
const TOMORROW_COLOR = 'rgb(237, 108, 2)';
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const dueDay = (value: DateTimeValue, showTime: boolean): string =>
  showTime ? readerDayKey(value) : formatFloating(value, 'date', DEFAULT_FULL_DATE_FORMAT);

const readerToday = (): string => readerDayKey(new Date());

export function formatDateOmitCurrentYear(value: DateTimeValue, showTime = false): string {
  const day = dueDay(value, showTime);
  if (!day) return '';
  const due = dayjs.utc(day);
  return due.format(due.year() === dayjs.utc(readerToday()).year() ? 'D MMM' : 'D MMM, YYYY');
}

function formatInBrowserZone(dueDate: Date, alwaysShowYear: boolean, showTime: boolean): DueDateDisplay {
  const currentDate = new Date();
  const currentYearFormat = alwaysShowYear ? 'D MMM, YYYY' : 'D MMM';
  const timeSuffix = showTime ? `, ${dayjs(dueDate).format('HH:mm')}` : '';

  if (
    dueDate.getDate() === currentDate.getDate() &&
    dueDate.getMonth() === currentDate.getMonth() &&
    dueDate.getFullYear() === currentDate.getFullYear()
  ) {
    return { name: `Today${timeSuffix}`, color: TODAY_COLOR };
  }

  const tomorrow = new Date(currentDate);
  tomorrow.setDate(currentDate.getDate() + 1);

  if (
    dueDate.getDate() === tomorrow.getDate() &&
    dueDate.getMonth() === tomorrow.getMonth() &&
    dueDate.getFullYear() === tomorrow.getFullYear()
  ) {
    return { name: `Tomorrow${timeSuffix}`, color: TOMORROW_COLOR };
  }

  const fiveDaysFromTomorrow = new Date(tomorrow);
  fiveDaysFromTomorrow.setDate(tomorrow.getDate() + 5);

  if (dueDate <= fiveDaysFromTomorrow && dueDate >= currentDate) {
    return { name: `${DAYS_OF_WEEK[dueDate.getDay()]}${timeSuffix}`, color: colors.text.secondary };
  }

  if (dueDate.getFullYear() === currentDate.getFullYear() && new Date(dueDate) < new Date(currentDate)) {
    return { name: `${dayjs(dueDate).format(currentYearFormat)}${timeSuffix}`, color: colors.error.main };
  }

  if (dueDate.getFullYear() === currentDate.getFullYear()) {
    return { name: `${dayjs(dueDate).format(currentYearFormat)}${timeSuffix}`, color: colors.text.secondary };
  }

  if (dueDate.getFullYear() > currentDate.getFullYear()) {
    return { name: `${dayjs(dueDate).format('D MMM, YYYY')}${timeSuffix}`, color: colors.text.secondary };
  }

  return { name: `${dayjs(dueDate).format('D MMM, YYYY')}${timeSuffix}`, color: colors.error.main };
}

// `showTime` is opt-in and only used by audit surfaces, whose due date is a moment: it appends the due
// time to every state. Tasks and Acknowledgements never pass it, so they stay date-only.
export function formatDueDate(
  value: string | number,
  { alwaysShowYear = false, showTime = false }: { alwaysShowYear?: boolean; showTime?: boolean } = {}
): DueDateDisplay {
  if (!timezoneStore.enabled) return formatInBrowserZone(new Date(value), alwaysShowYear, showTime);

  const day = dueDay(value, showTime);
  if (!day) return { name: '', color: colors.text.secondary };

  const due = dayjs.utc(day);
  const today = dayjs.utc(readerToday());
  const ahead = due.diff(today, 'day');
  const timeSuffix = showTime && !isTzFreeDate(value) ? `, ${formatMoment(value, DEFAULT_TIME_FORMAT)}` : '';

  if (ahead === 0) return { name: `Today${timeSuffix}`, color: TODAY_COLOR };
  if (ahead === 1) return { name: `Tomorrow${timeSuffix}`, color: TOMORROW_COLOR };
  if (ahead > 1 && ahead < 7) return { name: `${DAYS_OF_WEEK[due.day()]}${timeSuffix}`, color: colors.text.secondary };

  const pattern = due.year() === today.year() && !alwaysShowYear ? 'D MMM' : 'D MMM, YYYY';
  return { name: `${due.format(pattern)}${timeSuffix}`, color: ahead < 0 ? colors.error.main : colors.text.secondary };
}
